/**
 * The signed-in person's 12 / 24-hour clock preference (Settings → My account → Preferences), applied by the shared
 * formatter. Hooks for components; `getClockPref()` for plain functions (kept in sync from /me).
 */
import { DateTime } from 'luxon';
import { formatClock, formatClockRange, shiftTimeParts, isClockPref, type ClockPref } from '@tradeoutlet/shared';
import { useMe } from './hooks';

import { getClockPref, setClockPref } from './clockPref';

export { getClockPref, setClockPref };

export function useClockPref(): ClockPref {
  const v = useMe().data?.profile.clock_format;
  const p = isClockPref(v) ? v : '24h';
  setClockPref(p);
  return p;
}

const asDt = (iso: string, zone: string) => DateTime.fromISO(iso, { zone: 'utc' }).setZone(zone);
/** "14:30" / "2:30 PM" for a timestamp in a zone. */
export const clockAt = (iso: string, zone: string, pref = getClockPref(), compact = false) => formatClock(asDt(iso, zone), pref, compact);
/** "09:00–17:00" / "9:00 AM–5:00 PM" for two timestamps. */
export const clockRangeAt = (a: string, b: string, zone: string, pref = getClockPref(), compact = false) => formatClockRange(asDt(a, zone), asDt(b, zone), pref, compact);
/** Shift times with the overnight end day: { text: "22:00–06:00 Thu", endsLabel: "Ends Thu 15 Oct at 06:00" }. */
export const shiftParts = (s: { starts_at: string; ends_at: string }, zone: string, pref = getClockPref(), compact = false) => shiftTimeParts(asDt(s.starts_at, zone), asDt(s.ends_at, zone), pref, compact);
